import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Search from '../Search/Search';
import './FindInspiration.scss';

type SearchResult = {
  id: string;
  payload: {
    url?: string;
    title?: string;
    screenshotUrl?: string;
    videoUrl?: string;
    pageType?: string[];
    projectId?: string | null;
  };
};

function FindInspiration() {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const query = searchParams.get('q') || '';

  useEffect(() => {
    if (query) {
      handleSearch(query);
    }
  }, [query]);

  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await axios.post('https://uispider.softlite.io/api/v1/search/text', {
        query: searchQuery,
      });

      const filteredResults = (res.data?.results || []).filter((result: SearchResult | null) => result !== null);
      setResults(filteredResults);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Search failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const cleanTitle = (title: string | undefined): string => {
    if (!title) return 'Untitled';
    return title.replace(/webflow/gi, '').trim();
  };

  return (
    <div className="find-inspiration">
      <div className="find-inspiration__header">
        <h1>Website Design Inspiration</h1>
        <Search />
      </div>

      {loading && (
        <div className="find-inspiration__loading">
          <div className="spinner"></div>
          <p>Searching for inspiration...</p>
        </div>
      )}

      {error && <div className="find-inspiration__error">{error}</div>}

      {!loading && !error && results.length === 0 && query && (
        <div className="find-inspiration__empty">
          <p>No inspiration found. Try a different search term.</p>
        </div>
      )}

      {!loading && !error && results.length === 0 && !query && (
        <div className="find-inspiration__empty">
          <p>Enter a search query in the header to find inspiration.</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="find-inspiration__grid">
          {results.map((result) => (
            <div className="inspiration-card" key={result.id}>
              <div className="inspiration-card__video">
                {result.payload.videoUrl ? (
                  <video
                    src={result.payload.videoUrl}
                    poster={result.payload.screenshotUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                  />
                ) : result.payload.screenshotUrl ? (
                  <img 
                    src={result.payload.screenshotUrl} 
                    alt={result.payload.title || result.payload.url}
                    loading="lazy"
                  />
                ) : (
                  <div className="inspiration-card__no-media">No preview available</div>
                )}
              </div>
              <div className="inspiration-card__info">
                {result.payload.url ? (
                  <a
                    href={result.payload.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inspiration-card__title-link"
                  >
                    <h3 className="inspiration-card__title">
                      {cleanTitle(result.payload.title) || result.payload.url || 'Untitled'}
                    </h3>
                  </a>
                ) : (
                  <h3 className="inspiration-card__title">
                    {cleanTitle(result.payload.title) || 'Untitled'}
                  </h3>
                )}
                {result.payload.url && (
                  <a
                    href={result.payload.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inspiration-card__url"
                  >
                    Visit site →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FindInspiration;

